import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  Icon,
  Label,
  Popup,
  Pagination,
  Table,
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import {
  API,
  copy,
  showError,
  showInfo,
  showSuccess,
  showWarning,
  timestamp2string,
} from '../helpers';

import { ITEMS_PER_PAGE } from '../constants';
import { renderQuota } from '../helpers/render';
import globalStyles from '../styles/globalStyles';

function renderTimestamp(timestamp) {
  return <>{timestamp2string(timestamp)}</>;
}

function renderStatus(status, t) {
  switch (status) {
    case 1:
      return (
        <Label basic color='green'>
          {t('redemption.status.unused')}
        </Label>
      );
    case 2:
      return (
        <Label basic color='red'>
          {t('redemption.status.disabled')}
        </Label>
      );
    case 3:
      return (
        <Label basic color='grey'>
          {t('redemption.status.used')}
        </Label>
      );
    default:
      return (
        <Label basic color='black'>
          {t('redemption.status.unknown')}
        </Label>
      );
  }
}

const RedemptionsTable = () => {
  const { t } = useTranslation();
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);

  const loadRedemptions = async (startIdx) => {
    const res = await API.get(`/api/redemption/?p=${startIdx}`);
    const { success, message, data } = res.data;
    if (success) {
      if (startIdx === 0) {
        setRedemptions(data);
      } else {
        let newRedemptions = redemptions;
        newRedemptions.push(...data);
        setRedemptions(newRedemptions);
      }
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(redemptions.length / ITEMS_PER_PAGE) + 1) {
        // In this case we have to load more data and then append them.
        await loadRedemptions(activePage - 1);
      }
      setActivePage(activePage);
    })();
  };

  useEffect(() => {
    loadRedemptions(0)
      .then()
      .catch((reason) => {
        showError(reason);
      });
  }, []);

  const manageRedemption = async (id, action, idx) => {
    let data = { id };
    let res;
    switch (action) {
      case 'delete':
        res = await API.delete(`/api/redemption/${id}/`);
        break;
      case 'enable':
        data.status = 1;
        res = await API.put('/api/redemption/?status_only=true', data);
        break;
      case 'disable':
        data.status = 2;
        res = await API.put('/api/redemption/?status_only=true', data);
        break;
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess(t('token.messages.operation_success'));
      let redemption = res.data.data;
      let newRedemptions = [...redemptions];
      let realIdx = (activePage - 1) * ITEMS_PER_PAGE + idx;
      if (action === 'delete') {
        newRedemptions[realIdx].deleted = true;
      } else {
        newRedemptions[realIdx].status = redemption.status;
      }
      setRedemptions(newRedemptions);
    } else {
      showError(message);
    }
  };

  const searchRedemptions = async () => {
    if (searchKeyword === '') {
      // if keyword is blank, load files instead.
      await loadRedemptions(0);
      setActivePage(1);
      return;
    }
    setSearching(true);
    const res = await API.get(
      `/api/redemption/search?keyword=${searchKeyword}`
    );
    const { success, message, data } = res.data;
    if (success) {
      setRedemptions(data);
      setActivePage(1);
    } else {
      showError(message);
    }
    setSearching(false);
  };

  const handleKeywordChange = async (e, { value }) => {
    setSearchKeyword(value.trim());
  };

  const sortRedemption = (key) => {
    if (redemptions.length === 0) return;
    setLoading(true);
    let sortedRedemptions = [...redemptions];
    sortedRedemptions.sort((a, b) => {
      if (!isNaN(a[key])) {
        // If the value is numeric, subtract to sort
        return a[key] - b[key];
      } else {
        // If the value is not numeric, sort as strings
        return ('' + a[key]).localeCompare(b[key]);
      }
    });
    if (sortedRedemptions[0].id === redemptions[0].id) {
      sortedRedemptions.reverse();
    }
    setRedemptions(sortedRedemptions);
    setLoading(false);
  };

  const refresh = async () => {
    setLoading(true);
    await loadRedemptions(0);
    setActivePage(1);
  };

  return (
    <div style={globalStyles.page}>
      {/* 页面标题区域 */}
      <div style={{ ...globalStyles.hero, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name='dollar sign' style={{ color: '#18181b', fontSize: 22, margin: 0 }} />
          <span style={{ fontSize: 20, fontWeight: 800, color: '#18181b' }}>兑换码管理</span>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Label basic color='blue' style={{ borderRadius: 999 }}>总计 {redemptions.length}</Label>
        </div>
      </div>

      {/* 搜索区域 */}
      <div style={globalStyles.searchSection}>
        <Form onSubmit={searchRedemptions}>
          <Form.Input
            icon='search'
            fluid
            iconPosition='left'
            placeholder={t('redemption.search')}
            value={searchKeyword}
            loading={searching}
            onChange={handleKeywordChange}
            style={{ borderRadius: 10 }}
          />
        </Form>
      </div>

      {/* 表格区域 */}
      <div style={globalStyles.tableWrap}>
        <Table basic={'very'} compact size='small'>
          <Table.Header>
            <Table.Row style={globalStyles.tableHeader}>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortRedemption('id');
                }}
              >
                {t('redemption.table.id')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortRedemption('name');
                }}
              >
                {t('redemption.table.name')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortRedemption('status');
                }}
              >
                {t('redemption.table.status')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortRedemption('quota');
                }}
              >
                {t('redemption.table.quota')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortRedemption('created_time');
                }}
              >
                {t('redemption.table.created_time')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortRedemption('redeemed_time');
                }}
              >
                {t('redemption.table.redeemed_time')}
              </Table.HeaderCell>
              <Table.HeaderCell>{t('redemption.table.actions')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {redemptions
              .slice(
                (activePage - 1) * ITEMS_PER_PAGE,
                activePage * ITEMS_PER_PAGE
              )
              .map((redemption, idx) => {
                if (redemption.deleted) return <></>;
                return (
                  <Table.Row
                    key={redemption.id}
                    style={{
                      ...globalStyles.tableRow,
                      ...(hoveredRow === idx ? globalStyles.tableRowHover : {}),
                    }}
                    onMouseEnter={() => setHoveredRow(idx)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <Table.Cell>{redemption.id}</Table.Cell>
                    <Table.Cell>
                      {redemption.name ? redemption.name : t('redemption.table.no_name')}
                    </Table.Cell>
                    <Table.Cell>{renderStatus(redemption.status, t)}</Table.Cell>
                    <Table.Cell>{renderQuota(redemption.quota, t)}</Table.Cell>
                    <Table.Cell>
                      {renderTimestamp(redemption.created_time)}
                    </Table.Cell>
                    <Table.Cell>
                      {redemption.redeemed_time
                        ? renderTimestamp(redemption.redeemed_time)
                        : t('redemption.table.not_redeemed')}{' '}
                    </Table.Cell>
                    <Table.Cell>
                      <div>
                        <Button
                          size={'tiny'}
                          positive
                          style={globalStyles.button}
                          onClick={async () => {
                            if (await copy(redemption.key)) {
                              showSuccess(t('token.messages.copy_success'));
                            } else {
                              showWarning(t('token.messages.copy_failed'));
                              setSearchKeyword(redemption.key);
                            }
                          }}
                        >
                          {t('redemption.buttons.copy')}
                        </Button>
                        <Popup
                          trigger={
                            <Button size='tiny' negative style={globalStyles.button}>
                              {t('redemption.buttons.delete')}
                            </Button>
                          }
                          on='click'
                          flowing
                          hoverable
                        >
                          <Button
                            negative
                            style={globalStyles.button}
                            onClick={() => {
                              manageRedemption(redemption.id, 'delete', idx);
                            }}
                          >
                            {t('redemption.buttons.confirm_delete')}
                          </Button>
                        </Popup>
                        <Button
                          size={'tiny'}
                          disabled={redemption.status === 3}
                          style={globalStyles.button}
                          onClick={() => {
                            manageRedemption(
                              redemption.id,
                              redemption.status === 1 ? 'disable' : 'enable',
                              idx
                            );
                          }}
                        >
                          {redemption.status === 1
                            ? t('redemption.buttons.disable')
                            : t('redemption.buttons.enable')}
                        </Button>
                        <Button
                          size={'tiny'}
                          as={Link}
                          to={'/redemption/edit/' + redemption.id}
                          style={globalStyles.button}
                        >
                          {t('redemption.buttons.edit')}
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
          </Table.Body>

          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan='7'>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 12, columnGap: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <Button
                      size='small'
                      as={Link}
                      to='/redemption/add'
                      loading={loading}
                      style={globalStyles.button}
                    >
                      {t('redemption.buttons.add')}
                    </Button>
                    <Button size='small' onClick={refresh} loading={loading} style={globalStyles.button}>
                      {t('redemption.buttons.refresh')}
                    </Button>
                  </div>
                  <div style={{ marginLeft: 'auto' }}>
                    <Pagination
                      activePage={activePage}
                      onPageChange={onPaginationChange}
                      size='small'
                      siblingRange={1}
                      totalPages={
                        Math.ceil(redemptions.length / ITEMS_PER_PAGE) +
                        (redemptions.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                      }
                    />
                  </div>
                </div>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </div>
    </div>
  );
};

export default RedemptionsTable;
